import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import AudioVisualizer from 'components/AudioVisualizer';
import useAudioEvents from 'hooks/useAudioEvents';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotVoiceMessage');

const VoiceMessage = memo(({ audioData }) => {
    // ========================================
    // CONTEXT & CUSTOM HOOKS
    // ========================================
    const classes = useStyles();
    const { t } = useWeb5Context();
    const theme = useTheme();

    // ========================================
    // AUDIO DATA EXTRACTION
    // ========================================
    const {
        audioDuration: recordingDuration,
        batches,
        waveformData: recordingWaveform,
    } = audioData || {};

    // ========================================
    // REFS
    // ========================================
    const playbackTimeoutRef = useRef(null);
    const initializedBatchesRef = useRef(null);

    // ========================================
    // AUDIO HOOKS
    // ========================================
    const {
        audioBufferDuration,
        audioDuration,
        bindCanvas,
        error,
        handleTimeUpdate,
        initializeWaveform,
        isPlaying: isPlayingFromHook,
        level,
        playback,
        playbackContext,
        playbackStartTime,
        seek,
        stopPlayback,
    } = useAudioEvents({
        colors: {
            centerLine: theme.colors.actionMinor[100],
            waveform: theme.colors.actionMajor[150],
            waveformPlayed: theme.colors.actionMajor[500],
        },
    });

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const audioSizeKB = useMemo(() => {
        if (!batches || batches.length === 0) {
            return 0;
        }

        // Calculate size in KB
        // Base64 encoding increases size by ~33%, so we need to decode to get actual size
        const totalBase64Length = batches.reduce((sum, batch) => sum + batch.audio.length, 0);
        const approximateBytes = (totalBase64Length * 3) / 4; // Decode base64 to get byte size
        return (approximateBytes / 1024).toFixed(1);
    }, [batches]);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handlePlayPause = useCallback(() => {
        if (!batches) return;

        if (isPlayingFromHook) {
            stopPlayback();
            if (playbackTimeoutRef.current) {
                clearTimeout(playbackTimeoutRef.current);
                playbackTimeoutRef.current = null;
            }
        } else {
            playback(batches);
        }
    }, [batches, isPlayingFromHook, playback, stopPlayback]);

    const handleSeek = useCallback(
        (targetTime, resumePlayback) => {
            seek(targetTime, resumePlayback);
        },
        [seek],
    );

    // ========================================
    // MEMOIZED COMPONENT PROPS
    // ========================================
    const audioVisualizerProps = useMemo(
        () => ({
            audioBufferDuration,
            audioDuration,
            bindCanvas,
            height: 24,
            isActive: isPlayingFromHook,
            isPlaybackMode: true,
            isPlaying: isPlayingFromHook,
            level,
            onPlayPause: handlePlayPause,
            onSeek: handleSeek,
            onTimeUpdate: handleTimeUpdate,
            playbackContext,
            playbackStartTime,
        }),
        [
            audioBufferDuration,
            audioDuration,
            bindCanvas,
            handlePlayPause,
            handleSeek,
            handleTimeUpdate,
            isPlayingFromHook,
            level,
            playbackContext,
            playbackStartTime,
        ],
    );

    // ========================================
    // EFFECTS
    // ========================================
    // Initialize waveform when batches are available (only once per set of batches)
    useEffect(() => {
        if (batches && batches.length > 0) {
            // Check if we've already initialized with these batches
            const batchesChanged =
                !initializedBatchesRef.current ||
                initializedBatchesRef.current.length !== batches.length ||
                (initializedBatchesRef.current.length > 0 &&
                    initializedBatchesRef.current[0]?.audio !== batches[0]?.audio);

            if (batchesChanged) {
                // Pass recording waveform and duration if available (from recording)
                // Otherwise it will generate from audio batches
                initializeWaveform(batches, recordingWaveform, recordingDuration);
                initializedBatchesRef.current = batches;
            }
        }
    }, [batches, initializeWaveform, recordingDuration, recordingWaveform]);

    useEffect(() => {
        // Cleanup on unmount
        return () => {
            stopPlayback();
            if (playbackTimeoutRef.current) {
                clearTimeout(playbackTimeoutRef.current);
            }
        };
    }, [stopPlayback]);

    // ========================================
    // RENDER
    // ========================================
    if (!batches || batches.length === 0) {
        return (
            <div className={classes.root}>
                <Text type="body" color={theme.colors.utility.textSecondary}>
                    {t('label_voice_message')}
                </Text>
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.root}>
                <Icon color={theme.colors.semantic.error500} name="warning" size="medium" />
                <Text type="body" color={theme.colors.semantic.error500}>
                    {t('label_playback_error')}
                </Text>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <AudioVisualizer {...audioVisualizerProps} />
            <div className={classes.metadata}>
                <Text type="caption" color={theme.colors.utility.textSecondary}>
                    {audioSizeKB} KB
                </Text>
            </div>
        </div>
    );
});

VoiceMessage.displayName = 'VoiceMessage';

VoiceMessage.propTypes = {
    audioData: PropTypes.shape({
        audioDuration: PropTypes.number,
        batches: PropTypes.arrayOf(
            PropTypes.shape({
                audio: PropTypes.string.isRequired,
                timestamp: PropTypes.number,
            }),
        ).isRequired,
        waveformData: PropTypes.arrayOf(PropTypes.number),
    }),
};

export default VoiceMessage;
